import React from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { connectHits } from 'react-instantsearch-dom';
import GlobeIcon from '../../images/globe-solid.svg';

import CenterIcon from '../../images/search/center.svg';
import ExternIcon from '../../images/search/extern.svg';
import HospitalIcon from '../../images/search/hospital.svg';
import MvzIcon from '../../images/search/mvz.svg';
import PressIcon from '../../images/search/press.svg';
import SingleIcon from '../../images/search/single.svg';
import TopicIcon from '../../images/search/topic.svg';
import TreatmentIcon from '../../images/search/treatment.svg';
import UserIcon from '../../images/search/user.svg';
import EventIcon from '../../images/search/event.svg';

import RowSet from '../primitives/grid/row-set';
import flattenSnippet from '../../utils/flatten-snippet';
import NoResults from '../search/no-results';
import AvatarModal from '../primitives/avatar-modal';
import Breadcrumbs from '../primitives/breadcrumbs';

interface SearchTileProps {
    title: string
    bodyText: string
    link: string
    icon?: React.ReactNode
    postType?: string
    breadcrumbs: {[key: string]: string}[]
}

const SearchTile = ({
    title, bodyText, link, icon, postType, breadcrumbs,
}: SearchTileProps): JSX.Element => {
    const url = link && new URL(link);
    const postTypesandUrls = [
        {
            postType: 'kontakt',
            text: 'Kontakte',
            url: '/kontakte',
        },
        {
            postType: 'post',
            text: 'Beiträge',
            url: '/presse',
        },
        {
            postType: 'job',
            text: 'Jobs',
            url: '/job',
        },
        {
            postType: 'einrichtung',
            text: 'Einrichtungen',
            url: '/einrichtungen',
        },
        {
            postType: 'veranstaltungen',
            text: 'Veranstaltungen',
            url: '/veranstaltungen',
        },
    ];
    return (
        <Link
            css={{
                display: 'flex',
            }}
            to={url?.pathname}
        >
            <div css={{
                marginTop: '6px',
                width: '32px',
                height: '32px',
                marginRight: '12px',
                flexShrink: 0,
                '@media (max-width: 767px)': {
                    width: '24px',
                    height: '24px',
                    marginRight: '6px',
                },
            }}
            >
                {icon || <PressIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />}
            </div>
            <div css={{ width: '100%' }}>
                {Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && !!url?.pathname && (
                    <div
                        css={{
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative',

                            '&::after': {
                                position: 'absolute',
                                content: '" "',
                                top: 0,
                                right: 0,
                                width: '60px',
                                height: '50px',
                                zIndex: 10,
                                background: 'linear-gradient(to right, transparent 0%, white 40%, white 100%)',
                                '@media(max-width: 479px)': {
                                    width: '40px',
                                },
                            },
                        }}
                    >
                        <Breadcrumbs
                            type="search-results"
                            crumbs={breadcrumbs}
                            postTypeCrumb={postTypesandUrls.find(item => item.postType === postType)}
                            sliceFirst={breadcrumbs?.length > 1}
                        />
                    </div>
                )}
                <div className="h5" dangerouslySetInnerHTML={{ __html: title }} />
                <div
                    css={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: '4',
                        lineClamp: '4',
                        WebkitBoxOrient: 'vertical',
                        '& ais-highlight-0000000000': {
                            backgroundColor: '#b4c3d7',
                            borderRadius: '4px',
                        },
                    }}
                    className="p2"
                    dangerouslySetInnerHTML={{ __html: bodyText }}
                />
            </div>
        </Link>
    );
};

const ContactSearchTile = ({
    title, bodyText, icon, job, description, contact, einrichtung, focus, singlePage, urllink,
}): JSX.Element => (
    <AvatarModal
        name={title}
        job={job}
        jobDescription={description}
        contact={contact}
        einrichtung={einrichtung}
        focus={focus}
        link={
            singlePage && urllink?.pathname ? urllink?.pathname : undefined
        }
    >
        <div css={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
        }}
        >
            <div css={{
                marginTop: '6px',
                width: '32px',
                height: '32px',
                marginRight: '12px',
                '@media (max-width: 767px)': {
                    width: '24px',
                    height: '24px',
                    marginRight: '6px',
                },
            }}
            >
                {icon || <PressIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />}
            </div>
            <div css={{ width: '100%' }}>
                <div className="h5" css={{ textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: title }} />
                <div
                    css={{
                        textAlign: 'left',
                        '& ais-highlight-0000000000': {
                            backgroundColor: '#b4c3d7',
                            borderRadius: '4px',
                        },
                    }}
                    className="p2"
                    dangerouslySetInnerHTML={{ __html: bodyText }}
                />
            </div>
        </div>
    </AvatarModal>
);

export default SearchTile;

const getTileIcon = (type: string, num?: number) => {
    switch (type) {
        case 'einrichtung':
            switch (num) {
                case 3: // Thema
                    return <TopicIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
                case 4: // Klinik
                    return <HospitalIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
                case 5: // Behandlungzentrum
                    return <CenterIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
                case 7: // Praxis (extern)
                    return <ExternIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
                case 29: // MVZ
                    return <MvzIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
                case 15: // Treatment
                    return <TreatmentIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
                default:
                    return <GlobeIcon />;
            }
        case 'page':
            return <SingleIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
        case 'post':
            return <PressIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
        case 'kontakt':
            return <UserIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
        case 'veranstaltungen':
            return <EventIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
        default:
            return <SingleIcon css={{ maxHeight: '100%', maxWidth: '100%', height: '100%', fill: 'rgb(230, 0, 5)' }} />;
    }
};

export const SearchTileHits = connectHits(({ hits }: any) => {
    const data = useStaticQuery(graphql`
    {
      allWpEinrichtung {
        nodes {
          uri
          databaseId
          title
          acfFields {
            fieldGroupName
            subtitle
            address
          }
          slug
          types {
            nodes {
              name
              slug
            }
          }
        }
      }
    }
  `);
    const supplementalData = data?.allWpEinrichtung?.nodes || [];

    return (
        <RowSet breakpoints={{ dt: { between: 36 } }}>
            {(hits && hits.length > 0) ? hits.map((hit: any, index: number) => {
                const flattedSnipped = flattenSnippet(hit._snippetResult);
                if (hit?.post_type === 'kontakt') {
                    const existingEinrichtung = hit?.einrichtung; // an array
                    const reparsedEinrichtung = (() => {
                        if (Array.isArray(existingEinrichtung)) {
                            return existingEinrichtung.map(({ ID, ...props }) => {
                                const foundSupplementalData = supplementalData.find(
                                    x => x.databaseId === ID,
                                );
                                if (foundSupplementalData) {
                                    return foundSupplementalData;
                                }
                                return null;
                            }).filter(x => !!x);
                        }
                        return [];
                    })();
                    const newDoctorEinrichtung = {
                        einrichtung: reparsedEinrichtung,
                    };
                    if (!hit.permalink.trim()) {
                        return null;
                    }
                    return (
                        <div>
                            <ContactSearchTile
                                key={index}
                                title={hit?.post_title}
                                bodyText={flattedSnipped}
                                icon={getTileIcon(hit?.post_type, hit?.type)}
                                link={hit?.permalink}
                                job={hit?.job}
                                description={hit?.job_description}
                                contact={hit?.contact}
                                singlePage={hit?.single_page}
                                einrichtung={newDoctorEinrichtung?.einrichtung}
                                focus={hit?.focus}
                                hasSinglePage={hit?.single_page}
                            />
                        </div>
                    );
                }
                return (
                    <SearchTile
                        title={hit?.post_title}
                        bodyText={flattedSnipped}
                        icon={getTileIcon(hit?.post_type, hit?.type)}
                        link={hit?.permalink}
                        postType={hit?.post_type}
                        breadcrumbs={hit?.breadcrumbs}
                    />
                );
            }) : (
                <NoResults />
            )}
        </RowSet>
    );
});
